import { useReminders } from '@/store/reminders/hooks';
import { useUserId } from '@/hooks/auth';
import { ModalWrapper } from '../base/ModalWrapper';
import { useState, useCallback, useMemo } from 'react';
import { DashboardCard } from '../base/DashboardCard';
import EmptyMessageCard from '../base/EmptyMessageCard';
import ReminderCard from './ReminderCard';
import type { Reminder } from '@/store/reminders/types';
import { useReminderModal } from '@/components/reminders/context';
import { Empty } from '@/components/common/empty';

export function UpcomingReminders() {
  const userId = useUserId() || '';
  const limit = 5;
  const { loading, upcoming, active } = useReminders(userId);
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const {
    setOpen: setIsReminderModalOpen,
    setCurrentReminder: setSelectedReminder,
  } = useReminderModal();
  const sublist = useMemo(() => upcoming.slice(0, limit), [upcoming, limit]);

  const handleReminderClick = useCallback(
    (reminder: Reminder) => {
      setSelectedReminder(reminder);
      setIsReminderModalOpen(true);
    },
    [setSelectedReminder, setIsReminderModalOpen],
  );

  const handleViewAll = useCallback(() => {
    setIsViewAllOpen(true);
  }, []);

  const handleCloseViewAll = useCallback(() => {
    setIsViewAllOpen(false);
  }, []);

  return (
    <>
      <DashboardCard loading={loading}>
        <DashboardCard.Header
          title='Reminders'
          subtitle='your upcoming reminders'
          count={active.length}
          onViewAll={handleViewAll}
        />
        <DashboardCard.Body
          empty={upcoming.length === 0}
          emptyContent={
            <EmptyMessageCard
              icon='mdi:bell-outline'
              message="You're all caught up!"
            />
          }
        >
          {sublist.map((reminder) => (
            <div
              key={reminder.id}
              onClick={() => handleReminderClick(reminder)}
            >
              <ReminderCard
                icon='mdi:bell'
                data={reminder.data}
                isDismissed={Boolean(reminder.data.dismissed_at)}
              />
            </div>
          ))}
        </DashboardCard.Body>
      </DashboardCard>

      <ModalWrapper
        isOpen={isViewAllOpen}
        onClose={handleCloseViewAll}
        title='All Reminders'
        size='lg'
      >
        <div className='space-y-2'>
          {!active.length && (
            <Empty description="You're all caught up" icon='mdi:bell-outline' />
          )}
          {active.map((reminder) => (
            <div
              key={reminder.id}
              onClick={() => handleReminderClick(reminder)}
              className='cursor-pointer'
            >
              <ReminderCard
                icon='mdi:bell'
                data={reminder.data}
                isDismissed={Boolean(reminder.data.dismissed_at)}
              />
            </div>
          ))}
        </div>
      </ModalWrapper>
    </>
  );
}
